import { ALGO_LICENSE_ID } from 'lbs-pi-platform-access/es/data-server/image-analysis/macros/consts';
import { store } from 'shared/stores/core/decorators';
import { makeObservable } from 'shared/stores/core/make-observable';
import {translate} from 'shared/services/I18nService';
const $t = translate.scope('is.store');
@store('IA_MACROS')
export class MacroStore {
  state = {
    stateByImageId: {},
    macroInfoByMacroId: {}
  };

  getters = {
    currentState ({state}) {
      const {selectedImageId} = state('IMAGE');
      // Lazy load state if not present.
      if (!this.state.stateByImageId[selectedImageId]) {
        this.state.stateByImageId[selectedImageId] = createState();
      }

      return this.state.stateByImageId[selectedImageId];
    },
    selectedMacroId () {
      return this.getters.currentState.selectedMacroId;
    },
    selectedMacro () {
      return this.getters.currentState.macrosById[this.getters.selectedMacroId];
    },
    selectedMacroInfo () {
      return this.state.macroInfoByMacroId[this.getters.selectedMacroId];
    },
    isMacroSupported: () => ({id}) => this.getters.currentState.allowedMacroIds.includes(id)
  };

  remap = {
    'IMAGE_SELECTED': 'SELECT_MACRO_STATE'
  };

  // TODO: Rename to "CREATE_MACRO_STATE"
  SELECT_MACRO_STATE ({dispatch, state}) {
    // If we're already locked and loaded then just indicate that we've already selected a macro.
    if (this.getters.currentState.isLoaded && this.getters.selectedMacroId) {
      dispatch('IA_AFTER_SELECT_MACRO');
    }
  }

  // Command to loads macros from the server and updates the state for the image that was selected at the time this
  // call was initiated. Note that the IMAGE_SELECTED handler is concerned with which state is actually "current",
  // so the fact that this is async doesn't cause out of sync issues.
  async IA_LOAD_MACROS ({get, state, dispatch, resources}) {
    // ensure macro state is created
    dispatch('SELECT_MACRO_STATE');

    const {isVisible} = state('IA_STATE');
    const {imageId} = get('IA_STATE');

    if (!imageId || !isVisible) {
      throw new Error('Requested to load macros without an image or when IA module is hidden.');
    }

    const asyncStateRef = this.getters.currentState;

    if (!asyncStateRef.isLoaded && !asyncStateRef.isLoading) {
      // Create and store a state for this image.
      try {
        // Load the macro list for this image from the remote server.
        const macroProvider = resources.get('IA_MACROS');
        asyncStateRef.isLoading = true;
        const response = await macroProvider.list(imageId);
        asyncStateRef.isLoaded = true;
        response.data
          .filter(isMacroSupported)
          .forEach(macro => {
            asyncStateRef.allowedMacroIds.push(macro.id);
            asyncStateRef.macrosById[macro.id] = macro;
          });

        dispatch('IA_SELECT_DEFAULT_MACRO', {imageId});
      } catch (ex) {
        asyncStateRef.isLoading = false;
        let {asResult, asDetails, asMessage} = ex;
        asMessage = asMessage || ex.message;

        asyncStateRef.isLoaded = false;

        dispatch('IA_ERROR', {
          errorId: asResult,
          message: asMessage,
          details: asDetails
        });
      }
    }
  }

  IA_SELECT_DEFAULT_MACRO ({dispatch}, {imageId}) {
    const stateRef = this.state.stateByImageId[imageId];
    const macros = Object.values(stateRef.macrosById);
    // The selected macro is the one marked as default if present; or just the first one in the list if not.
    const macro = macros.find(macro => macro.isDefault) || stateRef.macrosById[stateRef.allowedMacroIds[0]];

    dispatch('IA_SELECT_MACRO', {imageId, macroId: macro.id});
  }

  IA_SELECT_MACRO ({state, dispatch}, {imageId, macroId}) {
    const {selectedImageId} = state('IMAGE');
    if (!imageId) {
      imageId = selectedImageId;
    }

    const stateRef = this.state.stateByImageId[imageId];

    if (macroId) {
      // If a macroId was passed in, check to see if it's supported before selecting it. If it's not, error and exit.
      if (!stateRef.allowedMacroIds.includes(macroId)) {
        dispatch('IA_ERROR', { message: $t('cannot-select-macro') });
        return;
      }

      this.state.macroInfoByMacroId[macroId] = this.state.macroInfoByMacroId[macroId] || createMacroInfoState();
    }

    stateRef.selectedMacroId = macroId || null;

    // Only dispatch if we've updated state for the active image/viewport
    if (imageId === selectedImageId) {
      dispatch('IA_AFTER_SELECT_MACRO');
    }
  }

  async IA_GET_MACRO_INFO ({state, resources}) {
    // If we already have macro info there's no need to get it again
    if (this.getters.selectedMacroInfo.isLoaded) {
      return;
    }

    const macroProvider = resources.get('IA_MACROS');
    const selectedMacroId = this.getters.selectedMacroId;

    const result = await macroProvider.get(selectedMacroId);

    const stateRef = this.state.macroInfoByMacroId[selectedMacroId];
    Object.assign(stateRef, result.data);
    stateRef.isLoaded = true;
  }
}

function createState () {
  return makeObservable({
    isLoaded: false,
    // Macro permissions are image specific, so it's possible for a user to be able to run macro on one
    // image but not another. That's why they're stored this way instead of being attached at the root level as one
    // might expect. TODO: Consider normalizing anyway.
    macrosById: {},
    // defaultMacroId: null, // maybe?
    // When converting the macroList response to a normalized object the original order is lost, so we store the ids
    // in order.
    allowedMacroIds: [],
    selectedMacroId: null
  });
}

function createMacroInfoState () {
  return makeObservable({
    // The observability of this isLoaded flag allows the rest of the data to refresh when accessed in a Vue computed
    // property without preloading an entire (fake) data structure
    isLoaded: false,
    isLoading: false
  });
}

// S-09791: Prevent use of algorithms ("other" algorithms) in WebViewer IA except for macros created from the IHC
export const SUPPORTED_ALGO_LICENSE_IDS = [
  ALGO_LICENSE_ID.IHC_NUCLEAR_V1,
  ALGO_LICENSE_ID.IHC_MEMBRANE_V1,
  ALGO_LICENSE_ID.POSITIVE_PIXEL_COUNT_V9,
  ALGO_LICENSE_ID.POSITIVE_PIXEL_COUNT_2004_08_11,
  ALGO_LICENSE_ID.POSITIVE_PIXEL_COUNT_FL_V1,
  ALGO_LICENSE_ID.NUCLEAR_V9,
  ALGO_LICENSE_ID.MEMBRANE_V9,
  ALGO_LICENSE_ID.RNA_ISH_V2
];

// Nuclear, IHC Membrane, RNA ISH v2, and the free Postitive Pixel Count algorithms
function isMacroSupported (macro) {
  return SUPPORTED_ALGO_LICENSE_IDS.includes(macro.algorithmLicenseId);
}



// WEBPACK FOOTER //
// ./src/shared/stores/image-analysis/macros/MacroStore.js